'use client';

import Cookies from 'js-cookie';
import { usePathname } from 'next/navigation';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { ParticipantEnum } from '@/constants/common';
import { useNavigate } from '@/hooks/useNavigate';
import {
  getParticipantByParam,
  getParticipantByPathname
} from '@/utils/helpers/participant';

type ParticipantContextProps = {
  isResumes: boolean;
  isVacancies: boolean;
  participant: ParticipantEnum;
  setParticipant: (participant: ParticipantEnum) => void;
  loading: boolean;
};

type ParticipantProviderProps = {
  cookieKey?: string;
  children: ReactNode;
  initialParticipant: ParticipantEnum;
  cookieDomain?: string;
};

const ParticipantContext = createContext<ParticipantContextProps | undefined>(
  undefined
);

const defaultContext: ParticipantContextProps = {
  isResumes: false,
  isVacancies: true,
  setParticipant: () => {},
  participant: ParticipantEnum.VACANCY,
  loading: false
};

export const useParticipant = () =>
  useContext(ParticipantContext) ?? defaultContext;

const applyParticipantAttr = (participant: ParticipantEnum) => {
  document.documentElement.setAttribute('data-participant', participant);
};

const Participant = ({
  cookieKey = 'participant',
  children,
  initialParticipant,
  cookieDomain
}: ParticipantProviderProps) => {
  const pathname = usePathname();
  const { navigate } = useNavigate();
  const [participant, setParticipantState] = useState<ParticipantEnum | null>(
    initialParticipant
  );
  const [loading, setLoading] = useState(true);

  const setParticipant = useCallback(
    (newParticipant: ParticipantEnum, withNavigate?: boolean) => {
      if (withNavigate) navigate({ participant: newParticipant });
      else setParticipantState(newParticipant);
      Cookies.set(cookieKey, newParticipant, {
        path: '/',
        domain: cookieDomain || undefined,
        expires: 30
      });
      applyParticipantAttr(newParticipant);
    },
    [cookieKey, cookieDomain, navigate]
  );

  // URL wins on the same render as the route change (no useEffect lag / SSR mismatch).
  const fromUrl = getParticipantByPathname(pathname);
  const resolvedParticipant = fromUrl ?? participant;
  const isResumes = resolvedParticipant === ParticipantEnum.RESUME;
  const isVacancies = resolvedParticipant === ParticipantEnum.VACANCY;

  const providerValue = useMemo(() => {
    if (resolvedParticipant === null) {
      return undefined;
    }

    return {
      participant: resolvedParticipant,
      setParticipant: (newParticipant: ParticipantEnum) =>
        setParticipant(newParticipant, true),
      isResumes,
      isVacancies,
      loading
    };
  }, [resolvedParticipant, setParticipant, loading, isResumes, isVacancies]);

  useEffect(() => {
    if (fromUrl) {
      // URL only — don't touch cookie (keeps homepage theme after Back)
      setParticipantState(fromUrl);
      applyParticipantAttr(fromUrl);
    } else if (pathname === '/') {
      setParticipant(
        getParticipantByParam(Cookies.get(cookieKey)) ?? ParticipantEnum.VACANCY
      );
    }

    setLoading(false);
  }, [cookieKey, setParticipant, pathname, fromUrl]);

  if (!providerValue) {
    return null;
  }

  return (
    <ParticipantContext.Provider value={providerValue}>
      {children}
    </ParticipantContext.Provider>
  );
};

export const ParticipantProvider = (props: ParticipantProviderProps) => {
  const context = useContext(ParticipantContext);

  if (context) {
    return <>{props.children}</>;
  }

  return <Participant {...props} />;
};
